import { groupBy } from 'lodash-es';

import {
  ICombo,
  IComboSlot,
  IComboSlotOption,
  IComboSlotSelection,
  IComboSlotSelections,
  IItem,
  IModifierSelection,
  IPicker,
} from '@rbi-ctg/menu';
import { ProductProps } from 'components/product-detail-list-item/product/types';
import { IProductDetailListItemProps } from 'components/product-detail-list-item/types';
import { MenuObjectTypes } from 'enums/menu';
import { computeProductCalories } from 'hooks/menu/utils';
import {
  IModifierSelectionActionWithData,
  IUserSelection,
  ProductWizardStep,
  UserSelectionModifierSlotMap,
} from 'state/product-wizard-v2/types';

import { computeModifierSelectionLabel, getModifierType } from './modifier/utils';
import { GetProductPropsSelectionsFn } from './types';

export const transformItemToProductListItemProps = (
  item: IItem | IPicker,
  extras?: {
    productNote?: string;
    isExpandable?: boolean;
    selections?: ProductProps['selections'];
    price?: number;
  }
): IProductDetailListItemProps => {
  const name = item.name?.locale ?? '';
  const image = item.image ? JSON.stringify(item.image) : undefined;
  return {
    isClickable: extras?.isExpandable,
    productProps: {
      name,
      calories: String(computeProductCalories(item as IItem)),
      image,
      isProductNameBold: true,
      selections: extras?.selections,
      price: extras?.price,
    },
  };
};

export const getProductPropsSelections: GetProductPropsSelectionsFn = ({
  modifierSelections = {},
  priceForItemOptionModifier,
}) => {
  const productPropsSelections: NonNullable<ProductProps['selections']> = [];
  Object.entries(modifierSelections).forEach(([, selectionsMap]) => {
    Object.values(selectionsMap).forEach(({ option, modifier, isSanityDefaultSelection, item }) => {
      if (isSanityDefaultSelection || modifier.quantity <= 0) {
        return;
      }

      const label = computeModifierSelectionLabel({
        modifierType: getModifierType(option),
        modifierOption: modifier,
        modifierName: option.name?.locale,
      });

      const modifierCalories = modifier.nutrition?.calories || 0;

      const modifierPrice = priceForItemOptionModifier({
        item,
        itemOption: option,
        modifier,
      });

      productPropsSelections.push({
        id: `${option._key}-${modifier._key}`,
        name: label,
        multiplier: modifier.modifierMultiplier?.multiplier ?? 0,
        price: modifierPrice,
        calories: Math.abs(modifierCalories),
      });
    });
  });
  return productPropsSelections;
};

export const getModifiersActions = (
  modifierSlotMap: UserSelectionModifierSlotMap
): IModifierSelectionActionWithData[] => {
  return Object.values(modifierSlotMap ?? {})
    .map(modifierActionsMap => Object.values(modifierActionsMap))
    .flat();
};

export const getComboSlotOptionFromItem = (
  comboSlot?: IComboSlot | null,
  selectedItem?: IItem | null
) => {
  return comboSlot?.options.find(opt => opt.option._id === selectedItem?._id);
};

/*
 * NOTE: The following transform utils are needed to covert to IComboSlotSelections + IModifierSelection type
 * These types are used in other helper methods such as - pricing, cart, nutrition, etc.
 * New wizard has a UserSelections type that is slightly different due to new designs
 * Action item - clean up/resolve the two different types so both can be supported outside wizard
 */

export const transformUserSelectionsComboToComboSlotSelections = (
  selections: IUserSelection['comboSlot'] | undefined,
  selectedProduct?: ICombo
): IComboSlotSelections => {
  if (!selections) {
    return {};
  }
  const legacySelections: IComboSlotSelections = {};
  const selectionsAsArrayWithoutSelectedItem = Array.from(selections.values()).filter(
    sel => sel.comboSlot && !sel.selectedItem
  );
  const selectionsAsArrayWithSelectedItem = Array.from(selections.values()).filter(
    sel => sel.comboSlot && sel.selectedItem
  );
  const selectionsGroupedByComboSlot = groupBy(
    selectionsAsArrayWithSelectedItem,
    sel => sel.comboSlot?._id
  );
  Object.entries(selectionsGroupedByComboSlot).forEach(([comboSlotId, value]) => {
    const itemsGroupedByItemId = groupBy(value, val => val.selectedItem?._id);
    const comboSlotOptionsSelections: IComboSlotSelection[] = Object.values(
      itemsGroupedByItemId
    ).map(values => ({
      option: getComboSlotOptionFromItem(
        values[0]?.comboSlot,
        values[0]?.selectedItem
      ) as IComboSlotOption,
      quantity: values.length,
    }));
    legacySelections[comboSlotId] = {
      data: value[0].comboSlot as IComboSlot,
      selections: comboSlotOptionsSelections,
    };
  });

  // When we have locked/collapsed combo slots
  if (selectionsAsArrayWithoutSelectedItem.length) {
    selectionsAsArrayWithoutSelectedItem.forEach(({ comboSlot }) => {
      const comboSlotId = comboSlot?._id;
      if (comboSlotId) {
        legacySelections[comboSlotId] = {
          data: comboSlot as IComboSlot,
          selections: (comboSlot?.options || []).map(opt => ({
            option: opt,
            quantity: opt.defaultAmount,
          })),
        };
      }
    });
  }

  // Include hidden slots that are omitted from IUserSelections state
  const hiddenSlots = (selectedProduct?.options || []).reduce((acc, slot) => {
    if (slot.uiPattern === 'hidden') {
      acc[slot._id] = {
        data: slot,
        selections: slot.options?.length
          ? [
              {
                option: slot.options[0],
                quantity: slot.options.length,
              },
            ]
          : [],
      };
    }
    return acc;
  }, {} as IComboSlotSelections);

  return {
    ...legacySelections,
    ...hiddenSlots,
  };
};

export const transformUserSelectionsModifierToModifierSelection = (
  selections: IUserSelection
): IModifierSelection[] =>
  Object.entries(selections.modifiers).flatMap(([modifierSlotKey, modifierSlotMap]) =>
    Object.values(modifierSlotMap).flatMap(modifierActionsMap =>
      Object.values(modifierActionsMap)
        .filter(({ modifier }) => modifier.quantity > 0)
        .map(({ itemId, modifier, option }) => ({
          _key: option._key,
          _type: option._type || MenuObjectTypes.ITEM_OPTION,
          comboSlotId: selections.comboSlot?.get(modifierSlotKey)?.comboSlot?._id,
          componentStyle: option.componentStyle,
          allowMultipleSelections: option.allowMultipleSelections,
          itemId,
          fifoPositions: [], // not being used in new wizard (first in first out concept)
          modifier,
          name: option.name,
          upsellModifier: option.upsellModifier,
          injectDefaultSelection: option.injectDefaultSelection,
        }))
    )
  );

/* END NOTE */

/*
 * transformProductWizardStepToEventAttr:
 *
 * Helper function to transform a Product Wizard Step into a valid separated by space string event attribute.
 * ( ProductHome -> Product Home )
 * ( ProductCustomization -> Product Customization )
 * ( ProductReview -> Product Review )
 * ( ProductEditOrder -> Product Edit Order )
 */
export const transformProductWizardStepToEventAttr = (currentStep: ProductWizardStep): string =>
  (ProductWizardStep[currentStep] ?? '').replace(/([a-z])([A-Z])/g, '$1 $2');

/*
 * transformComboSlotTypeToEventAttr:
 *
 * Helper function to transform a Combo Slot Type into a valid separated by space capitalized string event attribute.
 * ( pickerAspect -> Picker Aspect)
 * ( item -> Item )
 * ( sides -> Sides )
 * ( drinks -> Drinks )
 */
export const transformComboSlotTypeToEventAttr = (comboSlotType: string): string =>
  comboSlotType
    // Insert a space before all capital letters
    .replace(/([A-Z])/g, ' $1')
    // Capitalize the first letter for each word
    .replace(/\b\w/g, char => char.toUpperCase())
    // Trim extra space
    .trim();
